import React, { useRef, useEffect } from "react";
import cx from "classnames";
import { useDrag, useDrop } from "react-dnd";
import type { Identifier, XYCoord } from "dnd-core";
import { getEmptyImage } from "react-dnd-html5-backend";

import { FieldQueryItem, FieldQueryColumn } from "@common/components/field-query/field-query.types";

import { Cell } from "./components/cell";

import "./row.styles.scss";

export type RowProps = {
  item: FieldQueryItem;
  itemIndex: number;
  onClick: (item: FieldQueryItem) => void;
  selectedItem: boolean;
  columns: Array<FieldQueryColumn>;
  getId: {
    bodyRow: (params: { rowIndex: number }) => string;
    bodyRowCell: (params: { rowIndex: number; cellIndex: number }) => string;
  };
  className?: string;
  onRowDrop?: (dragIndex: number, hoverIndex: number) => void;
  enableDragDrop?: boolean;
};

type DragItem = {
  id: string | number;
  index: number;
  type: string;
};

const ITEM_TYPE = "FIELD_QUERY_ROW";

export const Row: React.FC<RowProps> = ({ item, onClick, columns, itemIndex, selectedItem, getId, className, onRowDrop, enableDragDrop = false}) => {
  const handleClick = React.useCallback(() => {
    onClick(item);
  }, [onClick, item]);

  const ref = useRef<HTMLTableRowElement>(null);

  const hasParentField = 'parentField' in item && item.parentField != null;
  const parentFieldName = hasParentField ? item.parentField.name : null;
  const ITEM_TYPE = hasParentField ? `FIELD_QUERY_ROW_WITH_PARENT_${parentFieldName}` : "FIELD_QUERY_ROW_WITHOUT_PARENT";

  // Drop functionality 
  const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null; isOver: boolean }>({
    accept: ITEM_TYPE,
    collect: (monitor) => ({
      handlerId: monitor.getHandlerId(),
      isOver: monitor.isOver(),
    }),
    drop(draggedItem, monitor) {
      if (!ref.current || !onRowDrop) return;

      const dragIndex = draggedItem.index;
      const hoverIndex = itemIndex;

      if (dragIndex === hoverIndex) return;

      onRowDrop(dragIndex, hoverIndex);
    },
  });

  // Drag functionality 
  const [{ isDragging }, drag, preview] = useDrag({
    type: ITEM_TYPE,
    item: () => ({ id: item.id, index: itemIndex }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: () => enableDragDrop,
  });

  useEffect(() => {
    if (enableDragDrop) {
      preview(getEmptyImage(), { captureDraggingState: true });
    }
  }, [preview, enableDragDrop]);

  if (enableDragDrop && onRowDrop) {
    drag(drop(ref));
  }

  return (
    <tr
      ref={ref}
      className={cx("FieldQueryTableBodyRow", {
        ["FieldQueryTableBodyRow--selected"]: selectedItem,
        ["FieldQueryTableBodyRow--dragging"]: isDragging,
      }, className)}
      id={getId.bodyRow({ rowIndex: itemIndex })}
      key={item.id}
      onClick={handleClick}
      data-handler-id={handlerId}
    >
      {columns.map((column, columnIndex) => (
        <Cell key={column.id} id={getId.bodyRowCell({ rowIndex: itemIndex, cellIndex: columnIndex })}>
          {column.component({ row: item, columnIndex, rowIndex: itemIndex })}
        </Cell>
      ))}
    </tr>
  );
};
